import { dateTimeFacade, dateTimeHelper } from '../utils';
import { decimalToTime } from './date';
import { DurationTranslations } from './duration-translations';

export type CfTimeInterval = 'base' | 'half' | 'hour' | 'increasing';

export const cfTimeIntervalMapping = {
  base: 15,
  half: 30,
  hour: 60,
  // For 'increasing' we start with the base value;
  // subsequent adjustments inside getTimeOptions will change it
  increasing: 15,
} as const;

export interface TimeOptions {
  value: string;
  label: string;
}

interface DurationConfig {
  minutes: number;
  gap: number;
}

function formatTime(hours: number, minutes: number, format: number): string {
  // eslint-disable-next-line no-untranslated-string/no-untranslated-string
  return `${('0' + (hours % format)).slice(-2)}:${('0' + Math.round(minutes)).slice(-2)}`;
}

function formatDuration(
  duration: number,
  daysAmount: number,
  durationTranslations: DurationTranslations,
): string {
  if (daysAmount) {
    return `(${daysAmount} ${durationTranslations.days} ${duration / 60} ${durationTranslations.hours})`;
  }

  return duration < 60
    ? `(${duration} ${durationTranslations.minutes})`
    : `(${duration / 60} ${durationTranslations.hours})`;
}

function getIncreasingInterval(currentInterval: number, _iteration: number): DurationConfig {
  // if (iteration === 4) {
  //   return { minutes: cfTimeIntervalMapping.half, gap: 15 };
  // }
  return { minutes: currentInterval, gap: 0 };
}

function generateTimeSlot(totalMinutes: number, format: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return formatTime(hours, minutes, format);
}

function generateTimeOptions(
  startMinutes: number,
  endMinutes: number,
  intervalMinutes: number,
  format: number,
  isIncreasing: boolean,
): string[] {
  const times: string[] = [];
  let currentMinutes = startMinutes;
  let currentInterval = intervalMinutes;
  let gap = 0;

  for (let i = 0; currentMinutes + currentInterval <= endMinutes + gap; i++) {
    if (isIncreasing) {
      const config = getIncreasingInterval(currentInterval, i);
      currentInterval = config.minutes;
      gap = config.gap;
    }

    times.push(generateTimeSlot(currentMinutes, format));
    currentMinutes += currentInterval;
  }

  return times;
}

interface TimeOptionsParams {
  startFrom?: number;
  endTime?: number;
  interval?: CfTimeInterval;
  timeFormat?: 12 | 24;
  daysAmount?: number;
  durationTranslations?: DurationTranslations;
}

export function getTimeOptions({
  startFrom = 0,
  endTime = 24,
  interval = 'half',
  timeFormat = 12,
  daysAmount = 0,
  durationTranslations = {
    minutes: '',
    hours: '',
    days: '',
  },
}: TimeOptionsParams): TimeOptions[] {
  const startMinutes = daysAmount ? 0 : startFrom * 60;
  const endMinutes = endTime * 60;
  const intervalMinutes = cfTimeIntervalMapping[interval];
  const isIncreasing = interval === 'increasing';

  const times = generateTimeOptions(startMinutes, endMinutes, intervalMinutes, 24, isIncreasing);

  return times.map((time) => {
    if (!isIncreasing) {
      return {
        label: dateTimeHelper.getTimeFromFormat(time, timeFormat),
        value: dateTimeHelper.getTimeFromFormat(time, 24),
      };
    }

    const duration = dateTimeFacade.getTimeDiff(time, decimalToTime(startFrom));
    const adjustedDuration =
      duration < 0 ? dateTimeFacade.getNextDayDuration(time, decimalToTime(startFrom)) : duration;

    const formattedTime = dateTimeHelper.getTimeFromFormat(time, timeFormat);
    const formattedDuration = formatDuration(adjustedDuration, daysAmount, durationTranslations);

    return {
      label: `${formattedTime} ${formattedDuration}`,
      value: formattedTime,
    };
  });
}
